import React from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowUpRight, ArrowLeft } from "lucide-react";
import { JOURNAL_POSTS, getPost } from "../data/journal";

function formatDate(iso) {
  return new Date(iso).toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
}

export function Journal() {
  const [featured, ...rest] = JOURNAL_POSTS;
  return (
    <div data-testid="journal-page">
      <section className="border-b border-border">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-20 md:py-28">
          <p className="label mb-3">Studio Journal</p>
          <h1 className="serif text-5xl md:text-7xl leading-[0.98] tracking-tight font-light max-w-4xl">
            Notes from the studio wall.
          </h1>
          <p className="mt-6 max-w-2xl text-[hsl(var(--ink-soft))] leading-relaxed text-lg">
            Long-form pieces on the work, the process, the history behind the
            paintings, and the collectors who take them home. Written by the
            hand that painted the piece.
          </p>
        </div>
      </section>

      {featured && (
        <section className="border-b border-border bg-[hsl(var(--surface))]">
          <Link
            to={`/journal/${featured.slug}`}
            className="block group"
            data-testid={`journal-featured-${featured.slug}`}
          >
            <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-16 md:py-24 grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-16 items-center">
              <div className="md:col-span-6 aspect-[4/5] overflow-hidden bg-background">
                <img
                  src={featured.hero}
                  alt={featured.title}
                  className="w-full h-full object-cover group-hover:scale-[1.02] transition-transform duration-700"
                />
              </div>
              <div className="md:col-span-6">
                <p className="label mb-3">Featured · {formatDate(featured.date)}</p>
                <h2 className="serif text-4xl md:text-5xl lg:text-6xl leading-[1.02] tracking-tight group-hover:italic transition-all">
                  {featured.title}
                </h2>
                <p className="mt-6 serif italic text-xl text-[hsl(var(--ink-soft))]">
                  {featured.dek}
                </p>
                <p className="mt-8 label inline-flex items-center gap-2">
                  Read the story <ArrowUpRight className="h-3.5 w-3.5" />
                </p>
              </div>
            </div>
          </Link>
        </section>
      )}

      <section>
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-16 md:py-24">
          <p className="label mb-8">Recent</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-10 md:gap-16">
            {rest.map((p) => (
              <Link
                key={p.slug}
                to={`/journal/${p.slug}`}
                className="block group"
                data-testid={`journal-card-${p.slug}`}
              >
                <div className="aspect-[16/10] overflow-hidden bg-[hsl(var(--surface))] mb-5">
                  <img
                    src={p.hero}
                    alt={p.title}
                    className="w-full h-full object-cover group-hover:scale-[1.02] transition-transform duration-700"
                  />
                </div>
                <p className="label mb-2">
                  {formatDate(p.date)} · {p.minutes} min read
                </p>
                <h3 className="serif text-2xl md:text-3xl leading-tight group-hover:italic transition-all">
                  {p.title}
                </h3>
                <p className="mt-3 text-[hsl(var(--ink-soft))]">{p.dek}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}

export function JournalPost() {
  const { slug } = useParams();
  const post = getPost(slug);
  if (!post) {
    return (
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-24">
        <Link to="/journal" className="label link-underline">
          ← All journal entries
        </Link>
        <h1 className="serif text-5xl md:text-6xl mt-8">Entry not found.</h1>
      </div>
    );
  }
  return (
    <article data-testid={`journal-post-${post.slug}`}>
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 pt-10 md:pt-16">
        <Link
          to="/journal"
          className="inline-flex items-center gap-2 label link-underline"
        >
          <ArrowLeft className="h-3.5 w-3.5" /> Journal
        </Link>
      </div>

      <header className="border-b border-border">
        <div className="max-w-[900px] mx-auto px-6 md:px-10 py-16 md:py-24">
          <p className="label mb-3">
            {formatDate(post.date)} · {post.minutes} min read
          </p>
          <h1 className="serif text-5xl md:text-7xl leading-[0.98] tracking-tight font-light">
            {post.title}
          </h1>
          <p className="mt-6 serif italic text-xl md:text-2xl text-[hsl(var(--ink-soft))]">
            {post.dek}
          </p>
        </div>
      </header>

      <div className="aspect-[16/9] max-h-[70vh] overflow-hidden bg-[hsl(var(--surface))]">
        <img
          src={post.hero}
          alt={post.title}
          className="w-full h-full object-cover"
        />
      </div>

      <div className="max-w-[720px] mx-auto px-6 md:px-10 py-16 md:py-24">
        <div
          className="serif text-lg md:text-xl leading-[1.7] text-foreground/90 space-y-6 [&>p]:leading-[1.7]"
          dangerouslySetInnerHTML={{ __html: post.body }}
        />

        <div className="mt-16 pt-10 border-t border-border flex items-center justify-between gap-4">
          <p className="label">
            Written in the studio · Denver, CO
          </p>
          <Link
            to="/journal"
            className="inline-flex items-center gap-2 label link-underline"
          >
            More entries <ArrowUpRight className="h-3.5 w-3.5" />
          </Link>
        </div>
      </div>
    </article>
  );
}
